import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarList, ColumnChart, fmtDate, fmtMoney, fmtNum, type BarDatum } from "@/components/reports/ReportWidgets";
import { cn } from "@/lib/utils";
import type { Answer, Group, Plan, Row } from "@/components/ai/askReport";

const inr = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

/** How the answer's main number is written: a share, money, or a plain count/amount */
export function valueFormat(a: Answer): (v: number | null | undefined) => string {
  return (v) => {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    if (a.measure === "RATE") return `${Math.round(v * 10) / 10}%`;
    if (a.value_money) return fmtMoney(v);
    return (Math.round(v * 10) / 10).toLocaleString("en-IN");
  };
}

/** The change against the period before, and how far along the target is */
export function ReportSummary({ answer }: { answer: Answer }) {
  const fmt = valueFormat(answer);
  const c = answer.compare;
  const t = answer.target;
  if (!c && !t) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {c && (
        <div className="rounded-lg border border-border px-3 py-2.5">
          <div className="text-xs text-muted-foreground">
            Against the {c.label} ({fmtDate(c.from)} – {fmtDate(c.to)})
          </div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <Change pct={c.change_pct} />
            <span className="text-xs text-muted-foreground tabular-nums">was {fmt(c.value)}</span>
          </div>
        </div>
      )}
      {t && (
        <div className="rounded-lg border border-border px-3 py-2.5">
          <div className="mb-1.5 text-xs text-muted-foreground">
            {t.label} · {t.period}
          </div>
          <Meter achieved={t.achieved} target={t.amount} money={t.money} />
        </div>
      )}
    </div>
  );
}

/** "▲ 12%" / "▼ 8%": the direction is said with an arrow and a word, not by colour alone */
function Change({ pct }: { pct: number | null }) {
  if (pct === null || !Number.isFinite(pct)) return <span className="text-sm font-semibold">New</span>;
  const up = pct > 0;
  const same = Math.abs(pct) < 0.05;
  return (
    <span className={cn("flex items-center gap-0.5 text-sm font-semibold tabular-nums", same ? "text-foreground" : up ? "text-success" : "text-destructive")} aria-label={same ? "No change" : `${up ? "Up" : "Down"} ${Math.abs(pct).toFixed(0)} percent`}>
      {!same && (up ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />)}
      {same ? "No change" : `${Math.abs(pct).toFixed(Math.abs(pct) < 10 ? 1 : 0)}%`}
    </span>
  );
}

/** Progress against a target, on the chart track (money or a count) */
function Meter({ achieved, target, money }: { achieved: number; target: number; money: boolean }) {
  if (!target) return <span className="text-xs text-muted-foreground">No target set for this period</span>;
  const pct = (achieved / target) * 100;
  const f = money ? fmtMoney : fmtNum;
  return (
    <div className="space-y-1">
      <div className="h-2 overflow-hidden rounded-full" style={{ background: "var(--viz-track)" }} role="meter" aria-valuemin={0} aria-valuemax={target} aria-valuenow={achieved} aria-label="Against target">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: "var(--viz-series-1)" }} />
      </div>
      <div className="flex justify-between text-[11px] tabular-nums text-muted-foreground">
        <span className="pr-2 font-semibold text-foreground">{pct.toFixed(0)}%</span>
        <span>
          {f(achieved)} of {f(target)}
        </span>
      </div>
    </div>
  );
}

/**
 * A one-way breakdown as a chart: ranked bars for categories (a pie or funnel asked for is drawn this way, it reads more
 * exactly), columns or a line over time. One series, so one hue and no legend; the table below carries every figure.
 */
export function ReportChart({ answer, plan }: { answer: Answer; plan: Plan }) {
  const groups = answer.groups || [];
  const fmt = valueFormat(answer);
  if (!groups.length || answer.group2_label || plan.chart === "TABLE") return null;
  const data: BarDatum[] = groups.map((g) => ({ label: g.label, value: g.value ?? 0, detail: detailOf(answer, g) }));
  const kind = plan.chart === "AUTO" || plan.chart === "PIE" || plan.chart === "FUNNEL" ? (answer.group_kind === "time" ? "LINE" : "BAR") : plan.chart;
  const format = (v: number) => fmt(v);
  if (answer.group_kind === "time") return kind === "LINE" ? <TrendLine data={data} format={format} /> : <ColumnChart data={data} format={format} height={180} />;
  return <BarList data={data.slice(0, 20)} format={format} />;
}

function detailOf(a: Answer, g: Group): string[] {
  const out = [`${g.count.toLocaleString("en-IN")} ${g.count === 1 ? "record" : "records"}`];
  if (a.measure === "RATE" && g.rate !== null) out.push(`${Math.round(((g.rate || 0) * g.count) / 100)} of ${g.count}`);
  if (g.qty !== undefined) out.push(`Quantity ${g.qty.toLocaleString("en-IN")}`);
  return out;
}

/** A single trend over time: a 2px line, small markers, recessive grid, and a crosshair with the value on hover */
function TrendLine({ data, format }: { data: BarDatum[]; format: (v: number) => string }) {
  const box = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 180;
  const pad = { l: 8, r: 8, t: 18, b: 22 };
  const max = Math.max(0, ...data.map((d) => d.value));
  if (!data.length || max === 0) return <p className="text-sm text-muted-foreground">Nothing to draw for this period.</p>;
  const x = (i: number) => pad.l + (data.length === 1 ? (W - pad.l - pad.r) / 2 : (i * (W - pad.l - pad.r)) / (data.length - 1));
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const path = data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join(" ");
  const peak = data.findIndex((d) => d.value === max);
  const last = data.length - 1;
  const step = Math.max(1, Math.ceil(data.length / 8));
  const pick = (clientX: number) => {
    const r = box.current?.getBoundingClientRect();
    if (!r) return;
    const at = ((clientX - r.left) / r.width) * W;
    let best = 0;
    data.forEach((_, i) => Math.abs(x(i) - at) < Math.abs(x(best) - at) && (best = i));
    setHover(best);
  };
  const h = hover !== null ? data[hover] : null;
  // Labels at the two ends sit inside the chart instead of hanging past its edges
  const edge = (i: number) => (data.length > 1 && i === 0 ? "translate-x-0" : data.length > 1 && i === last ? "-translate-x-full" : "-translate-x-1/2");
  return (
    <div ref={box} className="relative" onMouseMove={(e) => pick(e.clientX)} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-[180px] w-full" role="img" aria-label={`Trend: ${data.map((d) => `${d.label} ${format(d.value)}`).join(", ")}`} preserveAspectRatio="none">
        {[0.5, 1].map((f) => (
          <line key={f} x1={pad.l} x2={W - pad.r} y1={y(max * f)} y2={y(max * f)} stroke="var(--border)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        ))}
        <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} stroke="var(--border)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={pad.t - 6} y2={y(0)} stroke="var(--muted-foreground)" strokeWidth={1} strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
        <path d={path} fill="none" stroke="var(--viz-series-1)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Markers and labels in HTML so they keep their size when the chart stretches */}
      {data.map((d, i) => (
        <span
          key={d.label + i}
          className="pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background"
          style={{ left: `${(x(i) / W) * 100}%`, top: y(d.value), background: hover === i ? "var(--viz-series-1-hover)" : "var(--viz-series-1)" }}
        />
      ))}
      {[peak, last]
        .filter((v, i, a) => a.indexOf(v) === i)
        .map((i) => {
          const v = data[i]?.value ?? 0;
          return (
            <span key={`v${i}`} className={cn("pointer-events-none absolute whitespace-nowrap text-[10px] font-semibold tabular-nums text-foreground", edge(i))} style={{ left: `${(x(i) / W) * 100}%`, top: y(v) - 18 }}>
              {format(v)}
            </span>
          );
        })}
      <div className="relative h-4">
        {data.map((d, i) =>
          i % step === 0 || i === last ? (
            <span key={`l${i}`} className={cn("absolute whitespace-nowrap text-[10px] text-muted-foreground", edge(i))} style={{ left: `${(x(i) / W) * 100}%` }}>
              {d.label}
            </span>
          ) : null,
        )}
      </div>
      {h && hover !== null && (
        <div role="status" className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md" style={{ left: `${(x(hover) / W) * 100}%`, top: y(h.value) - 10 }}>
          <div className="font-semibold tabular-nums text-foreground">{format(h.value)}</div>
          {(h.detail || []).map((l) => (
            <div key={l} className="tabular-nums text-muted-foreground">
              {l}
            </div>
          ))}
          <div className="text-muted-foreground">{h.label}</div>
        </div>
      )}
    </div>
  );
}

/** Every group's figures (one way), or a table of the two breakdowns with totals */
export function GroupTable({ answer }: { answer: Answer }) {
  const groups = answer.groups || [];
  const fmt = valueFormat(answer);
  if (!groups.length) return null;
  if (answer.group2_label) return <Pivot answer={answer} />;
  const showValue = answer.measure !== "COUNT" && answer.measure !== "LIST";
  const hasQty = groups.some((g) => g.qty !== undefined);
  const hasPrev = groups.some((g) => g.prev !== undefined);
  const hasTarget = groups.some((g) => g.target !== undefined);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{answer.group_label}</TableHead>
            <TableHead className="text-right">Records</TableHead>
            {showValue && <TableHead className="text-right">{answer.value_label}</TableHead>}
            {!showValue && answer.total !== null && groups.some((g) => (g.sum || 0) > 0) && <TableHead className="text-right">Value</TableHead>}
            {hasQty && <TableHead className="text-right">Quantity</TableHead>}
            {hasPrev && <TableHead className="text-right">Before</TableHead>}
            {hasPrev && <TableHead className="text-right">Change</TableHead>}
            {hasTarget && <TableHead className="text-right">Target</TableHead>}
            {hasTarget && <TableHead className="text-right">Reached</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((g) => {
            const pct = g.prev ? (((g.value || 0) - g.prev) / Math.abs(g.prev)) * 100 : null;
            const achieved = answer.target?.money ? g.sum || 0 : g.count;
            return (
              <TableRow key={g.key + g.label}>
                <TableCell className="text-[13px]">{g.label}</TableCell>
                <TableCell className="text-right font-mono text-xs">{g.count.toLocaleString("en-IN")}</TableCell>
                {showValue && <TableCell className="text-right font-mono text-xs">{fmt(g.value)}</TableCell>}
                {!showValue && answer.total !== null && groups.some((x) => (x.sum || 0) > 0) && <TableCell className="text-right font-mono text-xs">{g.sum ? inr(g.sum) : "—"}</TableCell>}
                {hasQty && <TableCell className="text-right font-mono text-xs">{(g.qty || 0).toLocaleString("en-IN")}</TableCell>}
                {hasPrev && <TableCell className="text-right font-mono text-xs">{fmt(g.prev)}</TableCell>}
                {hasPrev && (
                  <TableCell className="text-right">
                    <span className="inline-flex justify-end">
                      <Change pct={pct} />
                    </span>
                  </TableCell>
                )}
                {hasTarget && <TableCell className="text-right font-mono text-xs">{g.target ? (answer.target?.money ? inr(g.target) : g.target) : "—"}</TableCell>}
                {hasTarget && <TableCell className="text-right font-mono text-xs">{g.target ? `${Math.round((achieved / g.target) * 100)}%` : "—"}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

/** Two breakdowns as rows × columns; the busiest columns first, the rest folded into "Other" */
function Pivot({ answer }: { answer: Answer }) {
  const fmt = valueFormat(answer);
  const groups = answer.groups || [];
  const { rows, cols, cell, rowTotal, colTotal } = useMemo(() => {
    const byCol = new Map<string, number>();
    for (const g of groups) byCol.set(g.label2 || "", (byCol.get(g.label2 || "") || 0) + g.count);
    const ranked = [...byCol.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    const keep = ranked.slice(0, 8);
    const cols = ranked.length > 8 ? [...keep, "Other"] : keep;
    const colOf = (l: string) => (keep.includes(l) ? l : "Other");
    const rows: string[] = [];
    const cell = new Map<string, { count: number; sum: number; hits: number }>();
    const add = (k: string, g: Group) => {
      const c = cell.get(k) || { count: 0, sum: 0, hits: 0 };
      c.count += g.count;
      c.sum += g.sum || 0;
      c.hits += Math.round(((g.rate || 0) * g.count) / 100);
      cell.set(k, c);
    };
    for (const g of groups) {
      if (!rows.includes(g.label)) rows.push(g.label);
      add(`${g.label}|${colOf(g.label2 || "")}`, g);
      add(`${g.label}|*`, g);
      add(`*|${colOf(g.label2 || "")}`, g);
      add("*|*", g);
    }
    const rowTotal = (r: string) => cell.get(`${r}|*`);
    const colTotal = (c: string) => cell.get(`*|${c}`);
    return { rows, cols, cell, rowTotal, colTotal };
  }, [groups]);
  // Averages can't be added up across cells, so the table shows counts for them (the chart-free view still says so)
  const show = (c?: { count: number; sum: number; hits: number }) => {
    if (!c) return "—";
    if (answer.measure === "TOTAL") return fmt(c.sum);
    if (answer.measure === "RATE") return c.count ? fmt((c.hits / c.count) * 100) : "—";
    return c.count.toLocaleString("en-IN");
  };
  return (
    <div className="space-y-1">
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">
                {answer.group_label} \ {answer.group2_label}
              </TableHead>
              {cols.map((c) => (
                <TableHead key={c} className="whitespace-nowrap text-right">
                  {c}
                </TableHead>
              ))}
              <TableHead className="text-right">All</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r}>
                <TableCell className="whitespace-nowrap text-[13px]">{r}</TableCell>
                {cols.map((c) => (
                  <TableCell key={c} className="text-right font-mono text-xs">
                    {show(cell.get(`${r}|${c}`))}
                  </TableCell>
                ))}
                <TableCell className="text-right font-mono text-xs font-semibold">{show(rowTotal(r))}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="text-[13px] font-semibold">All</TableCell>
              {cols.map((c) => (
                <TableCell key={c} className="text-right font-mono text-xs font-semibold">
                  {show(colTotal(c))}
                </TableCell>
              ))}
              <TableCell className="text-right font-mono text-xs font-semibold">{show(cell.get("*|*"))}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {answer.measure === "AVERAGE" && <p className="text-xs text-muted-foreground">Averages don't add up across a table, so it shows how many in each.</p>}
    </div>
  );
}

/** The records, with the columns the person chose; a column heading sorts by it (asks the server, so all rows count) */
export function RecordsTable({ answer, plan, busy, onSort, onOpen }: { answer: Answer; plan: Plan; busy: boolean; onSort: (patch: Partial<Plan>) => void; onOpen: () => void }) {
  const rows = answer.rows || [];
  const cols = answer.columns || [];
  const hasAmount = rows.some((r) => r.amount != null);
  const desc = plan.sort === "NEWEST" || plan.sort === "AMOUNT_HIGH";
  const sortedBy = plan.sort_field ? plan.sort_field.toLowerCase() : plan.sort === "AMOUNT_HIGH" || plan.sort === "AMOUNT_LOW" ? "@amount" : "@date";
  const head = (key: string, label: string, patch: () => Partial<Plan>, right = false) => (
    <TableHead className={cn(right && "text-right")} aria-sort={sortedBy === key ? (desc ? "descending" : "ascending") : undefined}>
      <button type="button" disabled={busy} onClick={() => onSort(patch())} className={cn("inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground", right && "flex-row-reverse")}>
        {label}
        {sortedBy === key ? desc ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" /> : <ChevronsUpDown className="size-3 opacity-40" />}
      </button>
    </TableHead>
  );
  const flip = (key: string) => (sortedBy === key ? !desc : true);
  if (!rows.length) return <p className="text-sm text-muted-foreground">Nothing matches. Take a condition off or widen the dates.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Record</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Owner</TableHead>
            {head("@date", answer.date_label || "Date", () => ({ sort_field: null, sort: flip("@date") ? "NEWEST" : "OLDEST" }))}
            {hasAmount && head("@amount", "Value", () => ({ sort_field: null, sort: flip("@amount") ? "AMOUNT_HIGH" : "AMOUNT_LOW" }), true)}
            {cols.map((c) =>
              c.key === "product" ? (
                <TableHead key={c.key}>{c.label}</TableHead>
              ) : (
                <React.Fragment key={c.key}>{head(c.key, c.label, () => ({ sort_field: c.key, sort: flip(c.key) ? "AMOUNT_HIGH" : "AMOUNT_LOW" }), c.type === "money" || c.type === "number")}</React.Fragment>
              ),
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r: Row) => (
            <TableRow key={r.id}>
              <TableCell className="min-w-[180px]">
                <Link to={r.link} onClick={onOpen} className="font-medium hover:text-primary hover:underline">
                  {r.title}
                </Link>
                {r.subtitle && <div className="text-xs text-muted-foreground">{r.subtitle}</div>}
              </TableCell>
              <TableCell className="whitespace-nowrap text-xs">{r.status || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">{r.owner || "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.date)}</TableCell>
              {hasAmount && <TableCell className="text-right font-mono text-xs">{r.amount != null ? inr(r.amount) : "—"}</TableCell>}
              {cols.map((c) => {
                const v = r.values?.[c.key];
                const shown = v === null || v === undefined || v === "" ? "—" : c.type === "money" ? inr(Number(v)) : c.type === "number" ? Number(v).toLocaleString("en-IN") : c.type === "date" ? fmtDate(String(v)) : String(v);
                return (
                  <TableCell key={c.key} className={cn("text-xs", c.type === "money" || c.type === "number" ? "text-right font-mono" : "max-w-[220px] truncate")} title={typeof v === "string" ? v : undefined}>
                    {shown}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
